/**
 * BulkSelector - Utility for managing bulk selection in tables/lists
 *
 * Extracted from contact_management.html (480 lines of inline JS).
 * Provides checkbox selection, select-all, and bulk actions functionality.
 *
 * @example
 * const selector = new BulkSelector({
 *   container: '#contacts-table',
 *   checkboxSelector: '.contact-checkbox',
 *   selectAllSelector: '#select-all',
 *   onSelectionChange: (selected) => console.log('Selected:', selected),
 * });
 */

export class BulkSelector {
  /**
   * @param {object} options - Configuration options
   * @param {string|Element} [options.container='body'] - Container element
   * @param {string} [options.checkboxSelector='.bulk-checkbox'] - Individual checkbox selector
   * @param {string} [options.selectAllSelector='#select-all'] - Select all checkbox selector
   * @param {string} [options.actionsBarSelector='.bulk-actions-bar'] - Actions bar selector
   * @param {string} [options.countSelector='.selected-count'] - Count display selector
   * @param {string} [options.valueAttribute='value'] - Attribute to get value from checkbox
   * @param {Function} [options.onSelectionChange] - Callback when selection changes
   */
  constructor(options = {}) {
    this.container = typeof options.container === 'string'
      ? document.querySelector(options.container)
      : (options.container || document.body);

    this.checkboxSelector = options.checkboxSelector || '.bulk-checkbox';
    this.selectAllSelector = options.selectAllSelector || '#select-all';
    this.actionsBarSelector = options.actionsBarSelector || '.bulk-actions-bar';
    this.countSelector = options.countSelector || '.selected-count';
    this.valueAttribute = options.valueAttribute || 'value';
    this.onSelectionChange = options.onSelectionChange || (() => {});

    this.selectedItems = new Set();

    this.init();
  }

  /**
   * Initialize event listeners
   */
  init() {
    // Select all checkbox
    const selectAll = this.container.querySelector(this.selectAllSelector);
    if (selectAll) {
      selectAll.addEventListener('change', (e) => this.toggleSelectAll(e.target.checked));
    }

    // Individual checkboxes (use event delegation for dynamic content)
    this.container.addEventListener('change', (e) => {
      if (e.target.matches(this.checkboxSelector)) {
        const value = e.target.getAttribute(this.valueAttribute) || e.target.value;
        this.toggleItem(value, e.target.checked);
      }
    });

    // Initialize from already checked items
    this.syncFromDOM();
  }

  /**
   * Sync selected items from DOM (for page reloads)
   */
  syncFromDOM() {
    const checkboxes = this.container.querySelectorAll(`${this.checkboxSelector}:checked`);
    checkboxes.forEach(cb => {
      const value = cb.getAttribute(this.valueAttribute) || cb.value;
      this.selectedItems.add(value);
    });
    this.updateUI();
  }

  /**
   * Toggle all items
   * @param {boolean} checked
   */
  toggleSelectAll(checked) {
    const checkboxes = this.container.querySelectorAll(this.checkboxSelector);

    checkboxes.forEach(cb => {
      cb.checked = checked;
      const value = cb.getAttribute(this.valueAttribute) || cb.value;

      if (checked) {
        this.selectedItems.add(value);
      } else {
        this.selectedItems.delete(value);
      }
    });

    this.updateUI();
    this.onSelectionChange(this.getSelected());
  }

  /**
   * Toggle a single item
   * @param {string} value
   * @param {boolean} checked
   */
  toggleItem(value, checked) {
    if (checked) {
      this.selectedItems.add(value);
    } else {
      this.selectedItems.delete(value);
    }

    this.updateSelectAllState();
    this.updateUI();
    this.onSelectionChange(this.getSelected());
  }

  /**
   * Update select-all checkbox state
   */
  updateSelectAllState() {
    const selectAll = this.container.querySelector(this.selectAllSelector);
    if (!selectAll) return;

    const checkboxes = this.container.querySelectorAll(this.checkboxSelector);
    const checkedCount = this.container.querySelectorAll(`${this.checkboxSelector}:checked`).length;

    if (checkedCount === 0) {
      selectAll.checked = false;
      selectAll.indeterminate = false;
    } else if (checkedCount === checkboxes.length) {
      selectAll.checked = true;
      selectAll.indeterminate = false;
    } else {
      selectAll.checked = false;
      selectAll.indeterminate = true;
    }
  }

  /**
   * Update UI elements (count, actions bar visibility)
   */
  updateUI() {
    const count = this.selectedItems.size;

    // Update count display
    const countEl = this.container.querySelector(this.countSelector);
    if (countEl) {
      countEl.textContent = count;
    }

    // Update actions bar visibility
    const actionsBar = this.container.querySelector(this.actionsBarSelector);
    if (actionsBar) {
      actionsBar.style.display = count > 0 ? '' : 'none';
      actionsBar.classList.toggle('is-visible', count > 0);
    }
  }

  /**
   * Get selected items as array
   * @returns {string[]}
   */
  getSelected() {
    return Array.from(this.selectedItems);
  }

  /**
   * Get selected count
   * @returns {number}
   */
  getCount() {
    return this.selectedItems.size;
  }

  /**
   * Check if item is selected
   * @param {string} value
   * @returns {boolean}
   */
  isSelected(value) {
    return this.selectedItems.has(value);
  }

  /**
   * Select specific items
   * @param {string[]} values
   */
  select(values) {
    values.forEach(value => {
      this.selectedItems.add(value);
      const cb = this.container.querySelector(`${this.checkboxSelector}[${this.valueAttribute}="${value}"]`);
      if (cb) cb.checked = true;
    });

    this.updateSelectAllState();
    this.updateUI();
    this.onSelectionChange(this.getSelected());
  }

  /**
   * Deselect specific items
   * @param {string[]} values
   */
  deselect(values) {
    values.forEach(value => {
      this.selectedItems.delete(value);
      const cb = this.container.querySelector(`${this.checkboxSelector}[${this.valueAttribute}="${value}"]`);
      if (cb) cb.checked = false;
    });

    this.updateSelectAllState();
    this.updateUI();
    this.onSelectionChange(this.getSelected());
  }

  /**
   * Clear all selections
   */
  clear() {
    this.selectedItems.clear();

    const checkboxes = this.container.querySelectorAll(this.checkboxSelector);
    checkboxes.forEach(cb => cb.checked = false);

    const selectAll = this.container.querySelector(this.selectAllSelector);
    if (selectAll) {
      selectAll.checked = false;
      selectAll.indeterminate = false;
    }

    this.updateUI();
    this.onSelectionChange(this.getSelected());
  }

  /**
   * Destroy instance and remove event listeners
   */
  destroy() {
    // Note: Since we use event delegation, we can't easily remove listeners
    // The container would need to be removed or this method expanded
    this.selectedItems.clear();
  }
}

export default BulkSelector;
